(() => {
  if (customElements.get('mbito-feature-media')) return;

  class FeatureMedia extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.poster = this.querySelector('img');
      this.button = this.querySelector('button');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.visible = false;
      this.requested = false;
      this.stopped = false;
      this.failed = false;
      this.video.muted = true;
      this.button.hidden = false;
      this.removeAttribute('data-playing');
      this.button.setAttribute('aria-label', this.dataset.playLabel);
      this.button.addEventListener('click', () => {
        this.stopped = !this.video.paused;
        this.requested = !this.stopped;
        this.update();
      }, options);
      this.video.addEventListener('playing', () => {
        if (!this.allowed()) return this.video.pause();
        this.setAttribute('data-playing', '');
        this.button.setAttribute('aria-label', this.dataset.pauseLabel);
      }, options);
      this.video.addEventListener('pause', () => this.showPoster(), options);
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.showPoster();
        this.button.hidden = true;
      }, options);
      this.motion.addEventListener('change', () => {
        this.requested = false;
        this.update();
      }, options);
      this.connection?.addEventListener('change', () => this.update(), options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio > 0;
        this.update();
      });
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this);
    }

    allowed() {
      return this.isConnected && this.visible && !document.hidden && !this.stopped && !this.failed &&
        (this.requested || (!this.motion.matches && !this.connection?.saveData));
    }

    update() {
      if (!this.allowed()) {
        this.video.pause();
        return;
      }
      // Choose enough pixels for the rendered image, including high-density screens.
      const width = this.poster.getBoundingClientRect().width * (devicePixelRatio || 1);
      const source = width <= 360 ? this.dataset.small : width <= 720 ? this.dataset.large : this.dataset.full;
      const style = getComputedStyle(this.poster);
      this.video.style.objectFit = style.objectFit;
      this.video.style.objectPosition = style.objectPosition;
      this.video.style.borderRadius = style.borderRadius;
      if (this.video.getAttribute('src') !== source) this.video.src = source;
      if (!this.video.paused || this.starting) return;
      this.starting = true;
      this.video.play().catch(() => {
        // Mobile power-saving modes can reject autoplay. Keep the poster and manual play button.
        this.stopped = true;
        this.showPoster();
      }).finally(() => { this.starting = false; });
    }

    showPoster() {
      this.removeAttribute('data-playing');
      this.button.setAttribute('aria-label', this.dataset.playLabel);
    }

    disconnectedCallback() {
      if (!this.video) return;
      this.abort.abort();
      this.observer.disconnect();
      this.resize.disconnect();
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    }
  }

  customElements.define('mbito-feature-media', FeatureMedia);
})();
